import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const Quote = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    quoteName: '',
    quoteType: '',
    status: '',
    customerId: '',
    customCustomerName: '',
    customCustomerEmail: '',
    notes: '',
  });
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);

  useEffect(() => {
    const fetchQuoteDetails = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Fetch quote details
        const { data: quoteData, error: quoteError } = await supabase
          .from('general_quotes')
          .select(`
            id,
            quote_name,
            created_at,
            file_path,
            created_by,
            customer_id,
            custom_customer_name,
            custom_customer_email,
            status,
            quote_type,
            notes,
            customers(id, nickname, billing_address, billing_city, billing_state, billing_zip, billing_contact_name, billing_contact_email)
          `)
          .eq('id', id)
          .eq('created_by', user.id)
          .single();
        if (quoteError) throw quoteError;
        if (!quoteData) throw new Error('Quote not found or you do not have access to it.');

        // Extract initial file name from file_path
        const initialFileName = quoteData.file_path ? quoteData.file_path.split('/').pop() : null;
        setQuote({ ...quoteData, initialFileName });

        setFormData({
          quoteName: quoteData.quote_name,
          quoteType: quoteData.quote_type,
          status: quoteData.status,
          customerId: quoteData.customer_id || '',
          customCustomerName: quoteData.custom_customer_name || '',
          customCustomerEmail: quoteData.custom_customer_email || '',
          notes: quoteData.notes || '',
        });
        setUseCustomCustomer(!!quoteData.custom_customer_name);

        // Fetch additional files
        const { data: filesData, error: filesError } = await supabase
          .from('general_quote_files')
          .select('id, file_name, file_path, uploaded_at')
          .eq('quote_id', id);
        if (filesError) throw filesError;
        setAdditionalFiles(filesData || []);

        // Fetch customers
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, nickname');
        if (customersError) throw customersError;
        setCustomers(customersData || []);
      } catch (err) {
        console.error('Error fetching quote details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuoteDetails();
  }, [user, id]);

  const handleEditToggle = () => {
    if (editMode) {
      setFormData({
        quoteName: quote.quote_name,
        quoteType: quote.quote_type,
        status: quote.status,
        customerId: quote.customer_id || '',
        customCustomerName: quote.custom_customer_name || '',
        customCustomerEmail: quote.custom_customer_email || '',
        notes: quote.notes || '',
      });
      setUseCustomCustomer(!!quote.custom_customer_name);
    }
    setEditMode(!editMode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const updatedData = {
        quote_name: formData.quoteName,
        quote_type: formData.quoteType,
        status: formData.status,
        customer_id: useCustomCustomer ? null : (formData.customerId || null),
        custom_customer_name: useCustomCustomer ? formData.customCustomerName : null,
        custom_customer_email: useCustomCustomer ? formData.customCustomerEmail : null,
        notes: formData.notes || null,
      };

      const { error } = await supabase
        .from('general_quotes')
        .update(updatedData)
        .eq('id', id)
        .eq('created_by', user.id);

      if (error) throw error;

      setQuote((prev) => ({
        ...prev,
        ...updatedData,
        customers: useCustomCustomer ? null : customers.find(c => c.id === parseInt(formData.customerId)),
      }));
      setEditMode(false);
      alert('Quote updated successfully!');
    } catch (err) {
      console.error('Error updating quote:', err);
      setError('Failed to update quote: ' + err.message);
    }
  };

  const handleFileUpload = async () => {
    if (!fileToUpload) {
      setError('Please select a file to upload.');
      return;
    }

    try {
      const fileName = `${Date.now()}-${fileToUpload.name}`;
      const filePath = `additional-files/${user.id}/${id}/${fileName}`;
      const { error: uploadError } = await supabase.storage
        .from('general-quote-files')
        .upload(filePath, fileToUpload);
      if (uploadError) throw uploadError;

      const { data, error: insertError } = await supabase
        .from('general_quote_files')
        .insert({
          quote_id: id,
          file_name: fileToUpload.name,
          file_path: filePath,
          uploaded_by: user.id,
        })
        .select();
      if (insertError) throw insertError;

      setAdditionalFiles([...additionalFiles, data[0]]);
      setFileToUpload(null);
      setIsUploadModalOpen(false);
      alert('File uploaded successfully!');
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file: ' + err.message);
    }
  };

  const handleFileDownload = async (filePath) => {
    try {
      const { data, error } = await supabase.storage
        .from('general-quote-files')
        .download(filePath);
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file.');
    }
  };

  const handleFileDelete = async (fileId, filePath) => {
    const userConfirmed = window.confirm('Are you sure you want to delete this file?');
    if (!userConfirmed) return;

    try {
      const { error: deleteError } = await supabase.storage
        .from('general-quote-files')
        .remove([filePath]);
      if (deleteError) throw deleteError;

      const { error: dbError } = await supabase
        .from('general_quote_files')
        .delete()
        .eq('id', fileId);
      if (dbError) throw dbError;

      setAdditionalFiles(additionalFiles.filter(file => file.id !== fileId));
      alert('File deleted successfully!');
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file: ' + err.message);
    }
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Accepted':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDeleteQuote = async () => {
    const userConfirmed = window.confirm('Are you sure you want to delete this quote? This action cannot be undone.');
    if (!userConfirmed) return;
  
    try {
      setLoading(true);
      
      // Delete all associated files from storage
      if (quote.file_path) {
        const { error: mainFileError } = await supabase.storage
          .from('general-quote-files')
          .remove([quote.file_path]);
        if (mainFileError) console.error('Error removing main file:', mainFileError);
      }
      
      // Delete all additional files from storage
      for (const file of additionalFiles) {
        const { error: fileError } = await supabase.storage
          .from('general-quote-files')
          .remove([file.file_path]);
        if (fileError) console.error(`Error removing file ${file.file_name}:`, fileError);
      }
      
      // Delete all additional files records from database
      if (additionalFiles.length > 0) {
        const { error: filesDbError } = await supabase
          .from('general_quote_files')
          .delete()
          .eq('quote_id', id);
        if (filesDbError) throw filesDbError;
      }
      
      // Delete the quote record
      const { error: quoteError } = await supabase
        .from('general_quotes')
        .delete()
        .eq('id', id)
        .eq('created_by', user.id);
      if (quoteError) throw quoteError;
      
      // Navigate back to quotes list
      navigate('/general-quotes');
      alert('Quote deleted successfully!');
    } catch (err) {
      console.error('Error deleting quote:', err);
      setError('Failed to delete quote: ' + err.message);
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;
  if (error) return <div className="text-center text-red-500 py-10">{error}</div>;
  if (!quote) return <div className="text-center text-red-500 py-10">Quote not found</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
  <h1 className="text-3xl font-bold text-gray-900">Quote: {quote.quote_name}</h1>
  <div className="flex space-x-2">
    <button
      onClick={handleDeleteQuote}
      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 shadow-sm"
    >
      Delete Quote
    </button>
    <button
      onClick={() => navigate('/general-quotes')}
      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
    >
      Back to Quotes
    </button>
  </div>
</div>

        {/* Quote Details Section */}
        <div className="bg-white rounded-lg p-6 mb-6 border-2 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Quote Details</h2>
            <button
              onClick={handleEditToggle}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 shadow-sm ${
                editMode
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {editMode ? 'Cancel' : 'Edit'}
            </button>
          </div>
          {editMode ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quote Name</label>
                <input
                  type="text"
                  name="quoteName"
                  value={formData.quoteName}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quote Type</label>
                <select
                  name="quoteType"
                  value={formData.quoteType}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                >
                  <option value="Fabrication">Fabrication</option>
                  <option value="Machine">Machine</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                >
                  <option value="In Progress">In Progress</option>
                  <option value="Sent">Sent</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <div className="flex items-center space-x-4 mb-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="customerType"
                      checked={!useCustomCustomer}
                      onChange={() => setUseCustomCustomer(false)}
                      className="mr-2"
                    />
                    Select Existing Customer
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="customerType"
                      checked={useCustomCustomer}
                      onChange={() => setUseCustomCustomer(true)}
                      className="mr-2"
                    />
                    Enter Custom Customer
                  </label>
                </div>
                {!useCustomCustomer ? (
                  <select
                    name="customerId"
                    value={formData.customerId}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  >
                    <option value="">Select Customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.nickname}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Custom Customer Name</label>
                      <input
                        type="text"
                        name="customCustomerName"
                        value={formData.customCustomerName}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                        required={useCustomCustomer}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Custom Customer Email</label>
                      <input
                        type="email"
                        name="customCustomerEmail"
                        value={formData.customCustomerEmail}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                        required={useCustomCustomer}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-sm"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Quote Name</label>
                <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{quote.quote_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created At</label>
                <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{new Date(quote.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Customer</label>
                <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">
                  {quote.custom_customer_name ? (
                    <>
                      {quote.custom_customer_name}
                      {quote.custom_customer_email && ` (${quote.custom_customer_email})`}
                    </>
                  ) : (
                    quote.customers?.nickname || 'N/A'
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusChipClass(quote.status)}`}>
                    {quote.status}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Quote Type</label>
                <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{quote.quote_type}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Initial File</label>
                <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">
                  {quote.initialFileName ? (
                    <button
                      onClick={() => handleFileDownload(quote.file_path)}
                      className="text-indigo-600 hover:text-indigo-800 transition duration-150"
                    >
                      {quote.initialFileName}
                    </button>
                  ) : (
                    '-'
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Notes Section */}
        <div className="bg-white rounded-lg p-6 mb-6 border-2 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
            <button
              onClick={handleEditToggle}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 shadow-sm ${
                editMode
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {editMode ? 'Cancel' : 'Edit'}
            </button>
          </div>
          {editMode ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  rows="4"
                  placeholder="Enter any notes for this quote..."
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-sm"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50 min-h-[100px]">{quote.notes || '-'}</p>
            </div>
          )}
        </div>

        {/* Additional Files Section */}
        <div className="bg-white rounded-lg p-6 border-2 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Additional Files</h2>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
            >
              Upload File
            </button>
          </div>
          {additionalFiles.length > 0 ? (
            <div className="overflow-x-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {additionalFiles.map((file) => (
                    <tr key={file.id} className="hover:bg-gray-50 transition duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{file.file_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(file.uploaded_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleFileDownload(file.file_path)}
                          className="text-indigo-600 hover:text-indigo-800 transition duration-150 mr-4"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => handleFileDelete(file.id, file.file_path)}
                          className="text-red-600 hover:text-red-800 transition duration-150"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No additional files uploaded for this quote.</p>
          )}
        </div>

        {/* File Upload Modal */}
        <Transition appear show={isUploadModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => setIsUploadModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Upload Additional File
                    </Dialog.Title>
                    <div className="mt-4">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">File</label>
                          <input
                            type="file"
                            onChange={(e) => setFileToUpload(e.target.files[0])}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-4">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                        onClick={() => {
                          setFileToUpload(null);
                          setIsUploadModalOpen(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                        onClick={handleFileUpload}
                      >
                        Upload
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
};

export default Quote;